/**
 * ───────────────────────────────────────────────────────────────────────────
 * RETURN-TO-BROWSE TICKET (single source of truth).
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Lives here rather than inside the marketplace page because TWO callers now
 * need it and they must agree exactly:
 *
 *   1. src/pages/Jobs.tsx        — writes the ticket when a project is opened,
 *                                  consumes it when the contractor comes back.
 *   2. src/components/ScrollToTop.tsx — must NOT force the page to the top when
 *                                  a valid ticket is waiting, otherwise the
 *                                  "open a project, press Back" journey loses
 *                                  the contractor's place.
 *
 * The ticket stores the scroll position TOGETHER with the query string it
 * belongs to and the moment it was taken, so restoring only happens when the
 * contractor returns to the SAME result set, soon. A fresh visit from the
 * header, a different filter combination or yesterday's session are ignored —
 * the marketplace must never open part-way down a list nobody has seen.
 *
 * sessionStorage is per-tab and cleared with the tab; a private-mode failure
 * simply means no restore, never a crash.
 */

const SCROLL_KEY = 'mkt_jobs_scroll';
const SCROLL_TTL_MS = 30 * 60 * 1000;

interface ScrollTicket {
  query?: string;
  y?: number;
  at?: number;
}

const readTicket = (): ScrollTicket | null => {
  try {
    const raw = sessionStorage.getItem(SCROLL_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ScrollTicket;
  } catch {
    return null;
  }
};

/** Remember where the contractor was, and which result set they were in. */
export const saveScroll = (query: string, y: number) => {
  try {
    sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ query, y, at: Date.now() }));
  } catch {
    /* private mode — restoring scroll is a convenience, never a requirement */
  }
};

/** Read AND consume the ticket. Returns 0 unless it is a fresh, matching one. */
export const takeScroll = (query: string): number => {
  const t = readTicket();
  try {
    sessionStorage.removeItem(SCROLL_KEY);
  } catch {
    /* ignore */
  }
  if (!t) return 0;
  if ((t.query || '') !== query) return 0;
  if (Date.now() - (t.at || 0) > SCROLL_TTL_MS) return 0;
  return Number(t.y || 0);
};

/**
 * True when an unexpired ticket is waiting — i.e. this navigation is a
 * deliberate return to browse results. Non-destructive: the ticket is only
 * consumed by takeScroll() once the grid has actually painted.
 */
export const hasScrollTicket = (): boolean => {
  const t = readTicket();
  if (!t) return false;
  if (!t.y || t.y <= 0) return false;
  return Date.now() - (t.at || 0) <= SCROLL_TTL_MS;
};
